import { useState } from 'react'

import Box from '@totallymoney/ui/components/Box'
import Modal from '@totallymoney/ui/components/Modal'
import Text from '@totallymoney/ui/components/Text'

import { ScoredCreditCardProduct } from '@appTypes/EligibilityApi.interface'

import {
    getFormattedAPR,
    getFormattedCurrency,
    getFormattedMonth,
} from '@utils/formatter'

import { StyledColumn, StyledTable } from './RatesAndFees.styled'
import Cell from './components/Cell'
import CellTooltip, { Content } from './components/CellTooltip'
import ColumnHeader from './components/ColumnHeader'

interface RatesAndFeesProps {
    card: ScoredCreditCardProduct
    guaranteedAPR?: number
}

const RatesAndFees = ({ card, guaranteedAPR }: RatesAndFeesProps) => {
    const [modalContent, setModalContent] = useState<Content | null>(null)

    return (
        <StyledTable
            gridTemplateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(4, 1fr)']}
        >
            <StyledColumn>
                <ColumnHeader
                    heading="Balance Transfer Offer"
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Balance Transfer Offer',
                                description:
                                    'Some cards offer a special introductory interest rate on balances transferred from another credit card. Rates and duration can differ depending on individual circumstance.',
                                ariaLabel: 'Balance Transfer Offer tooltip',
                                ariaDescription:
                                    'Find out what Balance Transfer Offer means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Intro Rate"
                    value={getFormattedAPR(
                        card.offerDetail.introBalanceTransferRateAER
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Balance Transfer Offer Intro Rate',
                                description:
                                    'The lowest interest rate you will be charged over the balance transfer period on any balance transferred to the card depending on individual circumstance.',
                                ariaLabel: 'Intro Rate tooltip',
                                ariaDescription:
                                    'Find out what Intro Rate means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Duration"
                    value={getFormattedMonth(
                        card.offerDetail.durationOfBalanceRateMonths,
                        'long'
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Duration',
                                description:
                                    'The length of time that you will be charged the introductory rate on any balance transferred to the card.',
                                ariaLabel: 'Duration tooltip',
                                ariaDescription: 'Find out what Duration means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Transfer Fee"
                    value={getFormattedAPR(
                        card.offerDetail.introBalanceTransferFee
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Transfer Fee',
                                description:
                                    'The one off handling fee that you will have to pay if you transfer a balance.',
                                ariaLabel: 'Transfer Fee tooltip',
                                ariaDescription:
                                    'Find out what Transfer Fee means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell />
                <Cell />
            </StyledColumn>
            <StyledColumn>
                <ColumnHeader
                    heading="Purchase Offer"
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Purchase Offer',
                                description:
                                    'Some cards offer a special introductory interest rate on spending on the card.',
                                ariaLabel: 'Purchase Offer tooltip',
                                ariaDescription:
                                    'Find out what Purchase Offer means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Intro Rate"
                    value={getFormattedAPR(
                        card.offerDetail.introPurchaseRateAER
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Intro Rate',
                                description:
                                    'The lowest interest rate you will be charged over the purchase period on any new spending on the card depending on individual circumstance.',
                                ariaLabel: 'Purchase Offer Intro Rate tooltip',
                                ariaDescription:
                                    'Find out what Intro Rate means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Duration"
                    value={getFormattedMonth(
                        card.offerDetail.durationOfPurchaseRateMonths,
                        'long'
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Duration',
                                description:
                                    'The length of time that you will be charged the introductory rate on new spending.',
                                ariaLabel: 'Purchase Offer Duration tooltip',
                                ariaDescription: 'Find out what Duration means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell />
                <Cell />
                <Cell />
            </StyledColumn>
            <StyledColumn>
                <ColumnHeader
                    heading="Annual Interest Rates"
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Annual Interest Rates',
                                description:
                                    'The Annual Interest Rate is the lowest interest rate that you will be charged on different types of borrowing after the end of any introductory offers. However, you may be charged higher rates depending on individual circumstance.',
                                ariaLabel: 'Annual Interest Rates tooltip',
                                ariaDescription:
                                    'Find out what Annual Interest Rate means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Balance Transfers"
                    value={getFormattedAPR(
                        card.offerDetail.balanceTransferAER,
                        card.guaranteedRate.guaranteedAPR != null &&
                            card.offerDetail.useGuaranteedAPR,
                        guaranteedAPR
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Balance Transfers',
                                description:
                                    'The lowest interest rate you will be charged on balance transfer after the end of any introductory offers depending on individual circumstance.',
                                ariaLabel: 'Balance Transfers tooltip',
                                ariaDescription:
                                    'Find out what Balance Transfers mean',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Purchases"
                    value={getFormattedAPR(
                        card.offerDetail.purchaseAER,
                        card.guaranteedRate.guaranteedAPR != null &&
                            card.offerDetail.useGuaranteedAPR,
                        guaranteedAPR
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Purchases',
                                description:
                                    'The lowest interest rate you will be charged on new spending after the end of any introductory offers depending on individual circumstance.',
                                ariaLabel: 'Purchases tooltip',
                                ariaDescription: 'Find out what Purchases mean',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Cash Withdrawals"
                    value={getFormattedAPR(
                        card.offerDetail.cashAdvanceAER,
                        card.guaranteedRate.guaranteedAPR != null &&
                            card.offerDetail.useGuaranteedAPR,
                        guaranteedAPR
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Cash Withdrawals',
                                description:
                                    'The lowest interest rate you will be charged on any cash withdrawals you make with the card depending on individual circumstance.',
                                ariaLabel: 'Cash Withdrawals tooltip',
                                ariaDescription:
                                    'Find out what Cash Withdrawals mean',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell />
                <Cell />
            </StyledColumn>
            <StyledColumn>
                <ColumnHeader heading="Other Key Facts" />
                <Cell
                    label="Annual Fee"
                    value={getFormattedCurrency(card.offerDetail.annualFee)}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Annual Fee',
                                description:
                                    'Some cards charge an annual fee, although the majority are fee free.',
                                ariaLabel: 'Annual Fee tooltip',
                                ariaDescription:
                                    'Find out what Annual Fee means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
                <Cell
                    label="Minimum Age"
                    value={
                        card.offerDetail.minAge
                            ? `${card.offerDetail.minAge} years`
                            : 'N/A'
                    }
                />
                <Cell
                    label="Minimum Income"
                    value={
                        card.offerDetail.minIncome
                            ? getFormattedCurrency(card.offerDetail.minIncome)
                            : 'Not Specified'
                    }
                />
                <Cell
                    label="Overseas Transaction Fee"
                    value={getFormattedAPR(
                        card.offerDetail.internationalCharges
                    )}
                    icon={
                        <CellTooltip
                            content={{
                                title: 'Overseas Transaction Fee',
                                description:
                                    'The proportion of any overseas transaction you make on the card that you will be charged as fee.',
                                ariaLabel: 'Overseas Transaction Fee tooltip',
                                ariaDescription:
                                    'Find out what Overseas Transaction Fee means',
                            }}
                            onPress={setModalContent}
                        />
                    }
                />
            </StyledColumn>
            <Modal
                title={modalContent?.title}
                isOpen={modalContent != null}
                hasTooltipHeader={true}
                onClose={() => setModalContent(null)}
            >
                <Box>
                    <Text>{modalContent?.description ?? ''}</Text>
                </Box>
            </Modal>
        </StyledTable>
    )
}

export default RatesAndFees
